import useDocumentTitle from "hooks/useDocumentTitle";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useGetSftpPartnerFilesQuery } from "store/queries/sftpPartners";
import {
  ISftpPartnerFile,
  SftpPartnerFileDirection,
  SftpPartnerFileStatus,
} from "types/SftpPartner";

import {
  SftpDownloadButton,
  SftpFileStatusTag,
  sftpFileStatusLabel,
  useSftpFileDownload,
} from "./components/SftpPartnerFiles/sftpFileParts";
import { WaitingInBucketCard } from "./components/SftpPartnerFiles/WaitingInBucketCard";
import { formatDateTime } from "./utils/hstImports";
import {
  ediFilesSearchPath,
  formatBytes,
  isoDateToLocalDate,
  localDateToIsoDate,
  outboundPeriodLabel,
  outboundReportTypeLabel,
  outboundSentByLabel,
  sftpPartnerPath,
} from "./utils/sftpPartners";

export const SFTP_FILES_PAGE_SIZE = 50;

// "All" is `null`, never `undefined`: an undefined option value makes PrimeReact pass the option object.
const DIRECTION_OPTIONS: {
  label: string;
  value: SftpPartnerFileDirection | null;
}[] = [
  { label: "All directions", value: null },
  { label: "Inbound", value: "inbound" },
  { label: "Outbound", value: "outbound" },
];

const STATUSES: SftpPartnerFileStatus[] = [
  "completed",
  "processing",
  "held",
  "failed",
  "conversion_deleted",
  "no_conversion",
  "succeeded",
];

const STATUS_OPTIONS: {
  label: string;
  value: SftpPartnerFileStatus | null;
}[] = [
  { label: "All statuses", value: null },
  ...STATUSES.map((s) => ({ label: sftpFileStatusLabel(s), value: s })),
];

const directionBody = (f: ISftpPartnerFile) => (
  <Tag
    value={f.direction === "inbound" ? "Inbound" : "Outbound"}
    severity={f.direction === "inbound" ? "info" : undefined}
    icon={f.direction === "inbound" ? "pi pi-arrow-down" : "pi pi-arrow-up"}
  />
);

const nameBody = (f: ISftpPartnerFile) => (
  <code title={f.s3Key} style={{ wordBreak: "break-all" }}>
    {f.name}
  </code>
);

const kindBody = (f: ISftpPartnerFile) => {
  if (f.direction === "outbound")
    return f.reportType ? outboundReportTypeLabel(f.reportType) : "—";
  return f.fileKind ?? "—";
};

const sizeBody = (f: ISftpPartnerFile) =>
  f.byteSize === null ? "—" : formatBytes(f.byteSize);

const atBody = (f: ISftpPartnerFile) => formatDateTime(f.at);

// Inbound: the conversion the key folded into (linked to EDI Files), or why there is none. Outbound: the
// period covered, rows written and who sent it.
const detailBody = (f: ISftpPartnerFile) => {
  if (f.direction === "outbound") {
    return (
      <span className="text-sm">
        {f.periodStart && f.periodEnd
          ? outboundPeriodLabel(f.periodStart, f.periodEnd)
          : "—"}
        {f.rowCount !== null ? ` · ${f.rowCount} rows` : ""}
        {` · ${outboundSentByLabel(f.triggeredByUserId)}`}
      </span>
    );
  }
  if (f.status === "conversion_deleted") {
    return (
      <span
        className="text-600 text-sm"
        title={`Conversion ${f.conversionId} no longer exists (deleted, or split into pieces)`}
        data-testid="sftp-file-conversion-deleted"
      >
        Conversion deleted
      </span>
    );
  }
  if (f.conversionDisplayName) {
    return (
      <Link
        to={ediFilesSearchPath(f.conversionDisplayName)}
        data-testid="sftp-file-conversion-link"
      >
        {f.conversionDisplayName}
      </Link>
    );
  }
  return "—";
};

const statusBody = (f: ISftpPartnerFile) => <SftpFileStatusTag file={f} />;

// CLE-2958 — SuperAdmin page listing one SFTP partner's files (`/integrations/sftp/:slug/files`): what is
// waiting in the bucket, and every file ingested from it or written to it, newest first.
export function SftpPartnerFiles() {
  const { slug = "" } = useParams();
  useDocumentTitle(`SFTP · ${slug} · Files`);
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState<SftpPartnerFileDirection | null>(
    null
  );
  const [status, setStatus] = useState<SftpPartnerFileStatus | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [name, setName] = useState("");

  // Search as the user types, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setName(nameInput.trim()), 400);
    return () => clearTimeout(t);
  }, [nameInput]);

  // Any filter change starts again at the first page.
  useEffect(() => {
    setPage(0);
  }, [direction, status, from, to, name]);

  const { data, isFetching, isError } = useGetSftpPartnerFilesQuery(
    {
      slug,
      page,
      pageSize: SFTP_FILES_PAGE_SIZE,
      ...(direction ? { direction } : {}),
      ...(status ? { status } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(name ? { name } : {}),
    },
    { skip: !slug }
  );
  const result = data?.data;
  const { download, pendingKey } = useSftpFileDownload(slug);

  const onDate = (setter: (v: string) => void) => (e: { value?: unknown }) =>
    setter(e.value instanceof Date ? localDateToIsoDate(e.value) : "");

  const actionBody = (f: ISftpPartnerFile) => (
    <SftpDownloadButton
      s3Key={f.s3Key}
      onDownload={download}
      loading={pendingKey === f.s3Key}
    />
  );

  return (
    <div className="p-4">
      <Link to={sftpPartnerPath(slug)} className="inline-block mb-3">
        <i className="pi pi-arrow-left mr-2" />
        {slug}
      </Link>
      <h1 className="mt-0 mb-3">{slug} · Files</h1>

      <WaitingInBucketCard slug={slug} />

      <Card title="Files" className="mb-3">
        <div className="text-600 text-sm mb-3">
          Inbound: every file the partner uploaded that an import has recorded,
          with the conversion it went into. Outbound: every report file we wrote
          for the partner. Times are when the file was processed or written;
          dates filter in UTC.
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <Dropdown
            value={direction}
            options={DIRECTION_OPTIONS}
            onChange={(e) => setDirection(e.value ?? null)}
            aria-label="Direction"
            data-testid="sftp-files-direction"
          />
          <Dropdown
            value={status}
            options={STATUS_OPTIONS}
            onChange={(e) => setStatus(e.value ?? null)}
            aria-label="Status"
            data-testid="sftp-files-status"
          />
          <Calendar
            value={from ? isoDateToLocalDate(from) : null}
            onChange={onDate(setFrom)}
            dateFormat="yy-mm-dd"
            placeholder="From"
            showButtonBar
            appendTo={document.body}
            inputId="sftp-files-from"
          />
          <Calendar
            value={to ? isoDateToLocalDate(to) : null}
            onChange={onDate(setTo)}
            dateFormat="yy-mm-dd"
            placeholder="To"
            showButtonBar
            appendTo={document.body}
            inputId="sftp-files-to"
          />
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="File name contains…"
              data-testid="sftp-files-name"
            />
          </span>
        </div>
        {isError ? (
          <Message
            severity="error"
            text="Couldn't load this partner's files."
          />
        ) : (
          <DataTable
            value={result?.files ?? []}
            dataKey="id"
            lazy
            paginator
            first={page * SFTP_FILES_PAGE_SIZE}
            rows={SFTP_FILES_PAGE_SIZE}
            totalRecords={result?.total ?? 0}
            onPage={(e: DataTablePFSEvent) =>
              setPage(Math.floor(e.first / SFTP_FILES_PAGE_SIZE))
            }
            loading={isFetching}
            emptyMessage="No files."
            responsiveLayout="scroll"
            data-testid="sftp-partner-files"
          >
            <Column header="Direction" body={directionBody} />
            <Column header="Name" body={nameBody} />
            <Column header="Kind" body={kindBody} />
            <Column header="Size" body={sizeBody} />
            <Column header="Processed / written" body={atBody} />
            <Column header="Conversion / report" body={detailBody} />
            <Column header="Status" body={statusBody} />
            <Column body={actionBody} style={{ width: "4rem" }} />
          </DataTable>
        )}
      </Card>
    </div>
  );
}
